import { useState, useRef, useEffect } from "react";
import { useImageLibrary } from "@/hooks/useImageLibrary";
import { Flex, Box, Grid, Text, Button } from "@radix-ui/themes";
import { cn } from "@/lib/utils";
import { EmptyCheckCircleIcon } from "@/assets/icons";
import type { ImageSourceDto } from "@/lib/lutely-api/gen/dtos/imageSourceDto";

type LibraryImagePanelProps = {
  creatorId: string;
  onSelect: (source: ImageSourceDto) => void;
};

export default function LibraryImagePanel({
  creatorId,
  onSelect,
}: LibraryImagePanelProps) {
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useImageLibrary(creatorId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Infinite scroll trigger
  useEffect(() => {
    if (!hasNextPage || !loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "100px" },
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  const images = data?.pages.flatMap((page) => page.content) ?? [];

  if (isLoading) {
    return (
      <Flex align="center" justify="center" className="h-[200px]">
        <Text size="2" color="gray">
          Loading images…
        </Text>
      </Flex>
    );
  }

  if (isError) {
    return (
      <Flex align="center" justify="center" className="h-[200px]">
        <Text size="2" color="red">
          Failed to load images
        </Text>
      </Flex>
    );
  }

  return (
    <>
      {images.length > 0 ? (
        <Grid
          columns="3"
          gap="2"
          className="w-full h-[400px] scrollbar-thin p-3 auto-rows-max"
        >
          {images.map((img) => {
            const isSelected = selectedId === img.id;

            return (
              <Box key={img.id} className="aspect-square w-full">
                <Button
                  type="button"
                  variant="ghost"
                  radius="medium"
                  data-testid="image-library-item"
                  onClick={() => {
                    setSelectedId(img.id);
                    onSelect(img.source);
                  }}
                  className={cn(
                    "w-full h-full min-h-0 p-0 overflow-hidden rounded-[2px]",
                    isSelected
                      ? "ring-2 ring-brand-primary bg-white"
                      : "hover:ring-2 hover:ring-gray-400",
                  )}
                >
                  {img.imageUrl ? (
                    <Box className="relative w-full h-full p-[2px] bg-white rounded-[2px]">
                      <img
                        src={img.imageUrl}
                        alt=""
                        className="w-full h-full object-cover rounded-[2px] block"
                      />

                      {isSelected && (
                        <>
                          <Box
                            aria-hidden
                            className="pointer-events-none absolute inset-[2px] rounded-[2px] bg-gray-600/30"
                          />
                          <Box
                            aria-hidden
                            className="pointer-events-none absolute inset-0 flex items-center justify-center"
                          >
                            <EmptyCheckCircleIcon className="w-[35%] h-[35%] text-white" />
                          </Box>
                        </>
                      )}
                    </Box>
                  ) : (
                    <Box className="w-full h-full bg-gray-200" />
                  )}
                </Button>
              </Box>
            );
          })}
          {hasNextPage && <Box ref={loadMoreRef} className="h-px w-full" />}
        </Grid>
      ) : (
        <Flex align="center" justify="center" className="w-full h-[400px]">
          <Text size="2" color="gray">
            No images yet
          </Text>
        </Flex>
      )}
    </>
  );
}
